import { StorefrontError } from '../core/error'
import { useCustomerStore } from '@shared/global-store/stores/customer/customer'
import { generateId, isClientData } from '@shared/shop-api/core/helper'
import { execute } from '@shared/shop-api/customer/execute-graphql'
import { getProducts } from '@shared/shop-api/product/api'
import { toWishlist } from '@shared/shop-api/wishlist/mapper'
import {
  customerWishlistQuery,
  setWishlistQuery,
} from '@shared/shop-api/wishlist/query'
import type { Wishlist, WishlistsInput } from '@shared/shop-api/wishlist/types'

import { trackError } from '@fond-of/telemetry'

export const getCustomerWishlists = async () => {
  const wishlistsData = await execute(customerWishlistQuery)

  const wishlists = wishlistsData?.customer?.metafield?.value

  if (!wishlists) {
    return []
  }

  try {
    return JSON.parse(wishlists).map(toWishlist) as Wishlist[]
  } catch (error) {
    throw new StorefrontError('Parsing wishlist data failed', error)
  }
}

export const setCustomerWishlists = async (
  wishlistsInput: WishlistsInput[]
) => {
  const { customer } = useCustomerStore()

  if (!customer?.id) {
    console.error('No customer id found')

    return
  }

  const wishlistData = await execute(setWishlistQuery, {
    customerId: customer?.id,
    wishlists: JSON.stringify(wishlistsInput),
  })

  const userErrors = wishlistData?.metafieldsSet?.userErrors

  if (userErrors?.length) {
    console.warn(
      '[Wishlist] Shopify metafieldsSet returned userErrors. Make sure the "custom.wishlists" metafield definition (type: json) exists for the Customer resource in your Shopify admin.',
      userErrors
    )
  }

  if (
    !isClientData(wishlistData) ||
    !wishlistData?.metafieldsSet?.wishlists?.[0]?.value
  ) {
    throw new StorefrontError(
      'Setting new wishlist values failed',
      wishlistData
    )
  }

  try {
    const newWishlists = wishlistData?.metafieldsSet?.wishlists?.[0].value
      ? JSON.parse(wishlistData?.metafieldsSet?.wishlists[0]?.value)?.map(
          toWishlist
        )
      : []

    return newWishlists as Wishlist[]
  } catch (error) {
    throw new StorefrontError('Parsing wishlist data failed', error)
  }
}

export const addWishlistToCustomer = async (wishlists: Wishlist[]) => {
  const id = generateId('wishlist_')

  wishlists.push({
    id,
    name: `Wishlist ${wishlists.length + 1}`,
    wishlistItems: [],
    createdAt: Date.now(),
  })

  const updatedWishlists = await setCustomerWishlists(wishlists)

  const wishlist = updatedWishlists?.find((wishlist) => wishlist.id === id)

  return wishlist
}

export const getWishlistById = async (wishlists: Wishlist[], id: string) => {
  return wishlists.find((wishlist) => wishlist.id === id)
}

export const addWishlistItem = async (
  wishlists: Wishlist[],
  wishlistId: string,
  productId: string
) => {
  const normalizedProductId = String(productId)
  const wishlistIndex = wishlists.findIndex(
    (wishlist) => wishlist.id === wishlistId
  )

  if (wishlistIndex === -1) {
    throw new StorefrontError(
      'Wishlist with given Id not found for item addition',
      wishlists
    )
  }

  if (
    !wishlists[wishlistIndex].wishlistItems?.some(
      (item) => String(item.id) === normalizedProductId
    )
  ) {
    wishlists[wishlistIndex].wishlistItems?.push({
      id: normalizedProductId,
    })
  }

  const updatedWishlists = await setCustomerWishlists(wishlists)

  return updatedWishlists?.find((wishlist) => wishlist.id === wishlistId)
}

export const deleteWishlistItem = async (
  wishlists: Wishlist[],
  wishlistId: string,
  wishlistItemId: string
) => {
  const normalizedWishlistItemId = String(wishlistItemId)
  const wishlistIndex = wishlists.findIndex(
    (wishlist) => wishlist.id === wishlistId
  )

  if (wishlistIndex === -1) {
    throw new StorefrontError(
      'Wishlist with given Id not found for item removal',
      wishlists
    )
  }

  wishlists[wishlistIndex].wishlistItems = wishlists[
    wishlistIndex
  ].wishlistItems?.filter(
    (item) => String(item.id) !== normalizedWishlistItemId
  )

  const updatedWishlists = await setCustomerWishlists(wishlists)

  return updatedWishlists?.find((wishlist) => wishlist.id === wishlistId)
}

export const removeStaleWishlistItems = async (
  wishlists: Wishlist[],
  wishlist: Wishlist
) => {
  const itemIds = wishlist.wishlistItems.map((item) => String(item.id))

  if (itemIds.length === 0) {
    return wishlist
  }

  try {
    const existingProducts = await getProducts(itemIds)
    const existingProductIds = new Set(
      existingProducts.map((product) => product.id.toString())
    )

    const validItems = wishlist.wishlistItems.filter((item) =>
      existingProductIds.has(String(item.id))
    )

    if (validItems.length === wishlist.wishlistItems.length) {
      return wishlist
    }

    const cleanedWishlist: Wishlist = {
      ...wishlist,
      wishlistItems: validItems,
    }

    const updatedWishlists = wishlists.map((w) =>
      w.id === wishlist.id ? cleanedWishlist : w
    )

    await setCustomerWishlists(updatedWishlists)

    return cleanedWishlist
  } catch (error) {
    trackError(error)

    return wishlist
  }
}
